import React from 'react'
import PropTypes from 'prop-types'
import { Radio, TimePicker, NumberInputDropdown, Button, WarningText, Popup } from 'components/UIElements'
import { connect } from 'react-redux'
import moment from 'moment'
import { pluralize } from 'utils/misc'
import _dayOptions from '../constants/schedulerConstants'
import VisibilityDisplay from './VisibilityDisplay'
import { schedulerActions } from '../../../modules/Instrument'
import VisibilityRangesDisplay from './VisibilityRangesDisplay'
import { DAYS_IN_MONTH, parseVisibilityTime } from 'utils/time'

class VisibilityContent extends React.Component {
  constructor(props) {
    super(props)
    this.state = { showWarning: false }
  }

  onToggleWarning = _showWarning => {
    const { showWarning } = this.state
    return () => {
      if (_showWarning === undefined) {
        this.setState({ showWarning: !showWarning })
      } else {
        this.setState({ showWarning: _showWarning })
      }
    }
  }

  onUpdateVisibleDays = (num, errorPath) => {
    const { recurring, updateRecurringField } = this.props
    const { showWarning } = this.state
    const { interval, scheme } = recurring
    const newInterval = scheme === 'n-months' ? interval * DAYS_IN_MONTH : interval
    if (num > newInterval) {
      if (!showWarning) this.onToggleWarning()()
    } else {
      updateRecurringField('visible_days', num, 'schedule.recurring.visible_days')
      if (showWarning) this.onToggleWarning()()
    }
  }

  render() {
    const { recurring } = this.props
    const { showWarning } = this.state
    const showVisibleDays = recurring && ['n-days', 'n-months'].includes(recurring.scheme)
    return (
      <div className='visibility-menu'>
        {showVisibleDays && (
          <VisibleDays
            onToggleWarning={this.onToggleWarning}
            onUpdateVisibleDays={this.onUpdateVisibleDays}
            showWarning={showWarning}
            {...this.props}
          />
        )}
        <Visibility {...this.props} />
      </div>
    )
  }
}

const VisibleDays = props => {
  const { recurring, onUpdateVisibleDays, onToggleWarning, errors, isReadOnlyState } = props
  const { interval, scheme } = recurring
  const newInterval = scheme === 'n-months' ? interval * DAYS_IN_MONTH : interval
  const dayOptions = _dayOptions.filter(option => option.key <= newInterval)
  return (
    <div className='visible-days'>
      <p>1. How many days should the instrument be visible for?</p>
      <NumberInputDropdown
        onOpen={onToggleWarning(false)}
        value={recurring.visible_days || ''}
        options={dayOptions}
        onChange={num => onUpdateVisibleDays(num, 'schedule.recurring.visible_days')}
        disabled={isReadOnlyState}
      />
      {` ${pluralize(recurring.visible_days, 'day', 'days', false)}`}
      <WarningText text={errors['schedule.recurring.visible_days']} />
      <p className='repeat-cycle-length'>
        {`Repeat Cycle Length: ${
          newInterval ? `${newInterval} ${pluralize(newInterval, 'day', 'days', false)}` : 'Repeat Interval Not Set'
        }`}
      </p>
      <VisibilityDisplay filled={recurring.visible_days} total={newInterval} />
    </div>
  )
}

const Visibility = props => {
  const {
    updateVisibility,
    addVisibilityRange,
    updateVisibilityRange,
    metadata,
    recurring,
    isReadOnlyState,
    errors,
    usePerScheduleVisibility,
    visibility,
    visibilityRanges,
  } = props
  const visibilityOptions = { onSchedule: usePerScheduleVisibility }
  const visibilityError = errors?.visibilityRanges
  return (
    <div className='visibility'>
      <div className='flexed start-justified'>
        <p>
          {`${
            recurring && recurring.scheme === 'n-days' ? '2. ' : ''
          }What time of the day should this instrument be visible? `}
        </p>
        <Popup hover noPointer dark position='bottom' trigger={<i className='fas fa-info-circle' />}>
          <p>
            Within the days that this instrument is available, participants will only be able to complete it during
            these hours.
          </p>
        </Popup>
      </div>
      <Radio
        selected={visibility === 'all_day'}
        onClick={() => {
          // Only change state if we're actually switching away from a different mode.
          // This prevents accidental resets from stray/bubbled clicks when already on "All Day".
          if (visibility !== 'all_day') {
            updateVisibility('visibility', 'all_day', visibilityOptions)
            updateVisibility('visibilityRanges', [{ start: null, end: null }], visibilityOptions)
          }
        }}
        id='all-day'
        content='All Day'
        disabled={isReadOnlyState}
      />
      <Radio
        selected={visibility === 'specific_time'}
        onClick={() => {
          // Only reinitialize ranges when switching from another mode to "Specific Time(s)".
          // If we're already on specific_time, keep the current ranges intact.
          if (visibility !== 'specific_time') {
            updateVisibility('visibility', 'specific_time', visibilityOptions)
            updateVisibility('visibilityRanges', [], visibilityOptions)
            addVisibilityRange(visibilityOptions)
          }
        }}
        id='specific-time'
        content='Specific Time(s)'
        disabled={isReadOnlyState}
      />
      {visibility === 'specific_time' && (
        <VisibilityRanges
          updateVisibility={updateVisibility}
          addVisibilityRange={addVisibilityRange}
          ranges={visibilityRanges}
          updateVisibilityRange={updateVisibilityRange}
          visibilityOptions={visibilityOptions}
        />
      )}
      {visibilityError && <WarningText text={visibilityError} />}
    </div>
  )
}

/**
 * GOLD-11299: Safely convert a time value to a moment object for TimePicker
 * Handles "Invalid date" strings and other invalid values by returning null.
 * Also accepts bare clock times from uploaded JSON (e.g. "5:00", "12:00").
 */
const safeTimeToMoment = timeValue => parseVisibilityTime(timeValue)

const VisibilityRanges = ({
  ranges,
  updateVisibilityRange,
  updateVisibility,
  addVisibilityRange,
  visibilityOptions = {},
}) => {
  // GOLD-11299: Filter out ranges with invalid dates for the display calculation
  const validRanges = ranges.filter(range => {
    if (!range) return false
    const { start, end } = range
    if (start === 'Invalid date' || end === 'Invalid date') return false
    if (start && !parseVisibilityTime(start)) return false
    if (end && !parseVisibilityTime(end)) return false
    return start !== null && end !== null
  })
  const visData = validRanges.length > 0 ? _getVisibilityData(validRanges) : null

  const onDelete = idx => () => {
    const newRanges = ranges.slice()
    newRanges.splice(idx, 1)
    updateVisibility('visibilityRanges', newRanges, visibilityOptions)
  }
  return (
    <div className='visibility-ranges'>
      {ranges.map((range, idx) => {
        const startMoment = safeTimeToMoment(range.start)
        const endMoment = safeTimeToMoment(range.end)
        const isLast = idx === ranges.length - 1
        return (
          <div key={`visibility-range-${idx}`} className='range-item'>
            <TimePicker
              id='from-time'
              disablePriorFromTime={endMoment}
              value={startMoment}
              onChange={mom => updateVisibilityRange(idx, 'start', mom, visibilityOptions)}
            />
            <span className='range-hyphen'>-</span>
            <TimePicker
              id='to-time'
              disablePriorToTime={startMoment}
              value={endMoment}
              onChange={mom => updateVisibilityRange(idx, 'end', mom, visibilityOptions)}
            />
            {isLast && (
              <Button
                className='grey'
                id='add-another-time'
                content='Add Another Time'
                onClick={() => addVisibilityRange(visibilityOptions)}
              />
            )}
            {!isLast && ranges.length > 1 && <i onClick={onDelete(idx)} className='clickable fas fa-times' />}
          </div>
        )
      })}
      {visData && <VisibilityRangesDisplay {...visData} />}
    </div>
  )
}

const _getDisabledTime = ({ start, end }) => {
  const disabledStart = { hour: [], min: [] }
  const disabledEnd = { hour: [], min: [] }
  const startHour = moment.utc(start).hour()
  const startMin = moment.utc(start).minute()
  const endHour = moment.utc(end).hour()
  const endMin = moment.utc(end).minute()
  for (let h = 0; h < 24; h++) {
    if (h > endHour) disabledStart.hour.push(h)
    if (h < startHour) disabledEnd.hour.push(h)
  }
  if (startHour !== endHour) return { disabledStart, disabledEnd }
  for (let m = 0; m < 60; m++) {
    if (m > endMin) disabledStart.min.push(m)
    if (m < startMin) disabledEnd.min.push(m)
  }
  return { disabledStart, disabledEnd }
}

const _getHourRanges = ranges => {
  return ranges.map(range => {
    const startTime = moment.utc(range.start)
    const startHour = startTime.hour()
    const startMinute = startTime.minute()
    const endTime = moment.utc(range.end)
    const endHour = endTime.hour()
    const endMinute = endTime.minute()
    return { start: startHour, startMinute, end: endHour, endMinute, startTime, endTime }
  })
}

const _getMinMax = hourRanges => {
  return {
    min: Math.min(...hourRanges.map(range => range.start)),
    max: Math.max(...hourRanges.map(range => (range.end && range.endMinute ? range.end + 1 : range.end))),
  }
}

const _getVisibilityData = ranges => {
  const hourRanges = _getHourRanges(ranges)
  if (!hourRanges.length) {
    return {
      min: 0,
      max: 24,
      visibleHours: new Array(24).fill(false),
      hourRanges: [],
      startTickValue: '',
      endTickValue: '',
    }
  }
  const hourRangesSortedByStart = [...hourRanges].sort((a, b) =>
    moment(a.startTime).isAfter(b.startTime, 'minute') ? 1 : -1,
  )
  const hourRangesSortedByEnd = [...hourRanges].sort((a, b) =>
    moment(a.endTime).isAfter(b.endTime, 'minute') ? 1 : -1,
  )

  const { min, max } = _getMinMax(hourRanges)
  const visibleHours = new Array(24).fill(false)
  hourRanges.forEach(range => {
    for (let i = range.start; i < range.end; i++) {
      if (i === range.start && range.startMinute) {
        visibleHours[i] = 'partial'
      } else if (i === range.end - 1) {
        visibleHours[i] = 'complete'
        visibleHours[i + 1] = range.endMinute ? 'reverse-partial' : false
      } else {
        visibleHours[i] = 'complete'
      }
    }
  })

  const startTickValue = hourRangesSortedByStart[0].startTime.format('H:mm')
  const endTickValue = hourRangesSortedByEnd[hourRangesSortedByEnd.length - 1].endTime.format('H:mm')

  return {
    min,
    max,
    visibleHours,
    hourRanges,
    startTickValue,
    endTickValue,
  }
}

VisibilityContent.propTypes = {
  recurring: PropTypes.shape({
    interval: PropTypes.number,
    scheme: PropTypes.string,
  }),
  updateRecurringField: PropTypes.func,
}

VisibleDays.propTypes = {
  errors: PropTypes.shape({
    'schedule.recurring.visible_days': PropTypes.string,
  }),
  onToggleWarning: PropTypes.func,
  onUpdateVisibleDays: PropTypes.func,
  recurring: PropTypes.shape({
    interval: PropTypes.number,
    scheme: PropTypes.string,
    visible_days: PropTypes.number,
  }),
}

Visibility.propTypes = {
  addVisibilityRange: PropTypes.func,
  errors: PropTypes.shape({
    visibilityRanges: PropTypes.string,
  }),
  metadata: PropTypes.shape({
    visibility: PropTypes.string,
    visibilityRanges: PropTypes.arrayOf(PropTypes.object),
  }),
  recurring: PropTypes.shape({
    scheme: PropTypes.string,
  }),
  updateVisibility: PropTypes.func,
  updateVisibilityRange: PropTypes.func,
  usePerScheduleVisibility: PropTypes.bool,
  visibility: PropTypes.string,
  visibilityRanges: PropTypes.arrayOf(PropTypes.object),
}

VisibilityRanges.propTypes = {
  addVisibilityRange: PropTypes.func,
  ranges: PropTypes.arrayOf(PropTypes.object),
  updateVisibility: PropTypes.func,
  updateVisibilityRange: PropTypes.func,
  visibilityOptions: PropTypes.object,
}

export default connect(({ instrumentReducer }) => {
  const { metadata } = instrumentReducer.instrument
  return {
    metadata,
    errors: instrumentReducer.errors,
  }
}, schedulerActions)(VisibilityContent)
